import { db } from "@/api/db/db.index";
import { readServerMarketingConsent } from "@/api/lib/marketing-consent";
import { paymentService } from "@/api/services/payment.service";
import { createServerFn } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";
import { PaymentStatus, payments } from "@thecoach/db";
import { and, eq } from "drizzle-orm";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

export const getVerifiedPaymentReceiptFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(z.object({ paymentId: z.string().min(1) }))
  .handler(async ({ context, data }) => {
    const [payment] = await db
      .select()
      .from(payments)
      .where(
        and(
          eq(payments.paymentId, data.paymentId),
          eq(payments.userId, context.auth.userId),
          eq(payments.status, PaymentStatus.SUCCESS),
        ),
      )
      .limit(1);
    if (!payment) return { status: 404 as const, body: null };
    const consent = readServerMarketingConsent(getRequestHeaders());
    return {
      status: 200 as const,
      body: {
        paymentId: payment.paymentId,
        itemType: payment.itemType,
        itemId:
          payment.itemType === "program"
            ? payment.programId
            : payment.itemType === "package"
              ? payment.packageId
              : payment.productId,
        amount: payment.amount,
        currency: "TRY" as const,
        marketingAllowed:
          payment.marketingConsentGranted &&
          Boolean(
            consent?.granted && consent.id === payment.marketingConsentId,
          ),
      },
    };
  });

function getClientIp(): string {
  try {
    const headers = getRequestHeaders();
    const forwardedFor = headers.get("x-forwarded-for");
    if (forwardedFor) {
      return forwardedFor.split(",")[0].trim();
    }
    return headers.get("x-real-ip") || "127.0.0.1";
  } catch {
    return "127.0.0.1";
  }
}

function sanitizePaymentError(_error: unknown): string {
  return "Online ödeme şu anda tamamlanamıyor. WhatsApp üzerinden satın alma veya Havale/EFT ile devam edebilirsiniz.";
}

const marketingFields = z.object({
  source: z.string().max(120).optional(),
  medium: z.string().max(120).optional(),
  campaign: z.string().max(200).optional(),
  gclid: z.string().max(300).optional(),
  fbclid: z.string().max(300).optional(),
  metaFbp: z.string().max(150).optional(),
  metaFbc: z.string().max(150).optional(),
});

function paymentMarketing(data: z.infer<typeof marketingFields>) {
  const headers = getRequestHeaders();
  const consent = readServerMarketingConsent(headers);
  if (!consent?.granted) return undefined;
  return {
    consentId: consent.id,
    attribution: JSON.stringify({
      source: data.source,
      medium: data.medium,
      campaign: data.campaign,
      gclid: data.gclid,
      fbclid: data.fbclid,
    }),
    fbp: data.metaFbp,
    fbc: data.metaFbc,
    userAgent: headers.get("user-agent")?.slice(0, 500),
  };
}

const createProgramCheckoutSchema = z
  .object({
    programId: z.string(),
  })
  .extend(marketingFields.shape);

export const createIyzicoCheckoutSessionFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(createProgramCheckoutSchema)
  .handler(async ({ context, data }) => {
    const clientIp = getClientIp();

    try {
      const result = await paymentService.createProgramPaymentSession({
        userId: context.auth.userId,
        programId: data.programId,
        clientIp,
        marketing: paymentMarketing(data),
      });

      if (result.status === "failure") {
        return {
          status: 400,
          body: { error: sanitizePaymentError(result.errorMessage) },
        };
      }

      return {
        status: 200,
        body: {
          paymentId: result.paymentId,
          amount: result.amount,
          token: result.token,
          paymentPageUrl: result.paymentPageUrl,
          checkoutFormContent: result.checkoutFormContent,
        },
      };
    } catch (error) {
      console.error("createIyzicoCheckoutSessionFn error:", error);
      return {
        status: 500,
        body: { error: sanitizePaymentError(error) },
      };
    }
  });

const createPackageCheckoutSchema = z
  .object({
    packageId: z.string(),
    durationMonths: z.number().optional(),
  })
  .extend(marketingFields.shape);

export const createPackagePaymentSessionFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(createPackageCheckoutSchema)
  .handler(async ({ context, data }) => {
    const clientIp = getClientIp();

    try {
      const result = await paymentService.createPackagePaymentSession({
        userId: context.auth.userId,
        packageId: data.packageId,
        durationMonths: data.durationMonths,
        clientIp,
        marketing: paymentMarketing(data),
      });

      if (result.status === "failure") {
        return {
          status: 400,
          body: { error: sanitizePaymentError(result.errorMessage) },
        };
      }

      return {
        status: 200,
        body: {
          paymentId: result.paymentId,
          amount: result.amount,
          token: result.token,
          paymentPageUrl: result.paymentPageUrl,
          checkoutFormContent: result.checkoutFormContent,
        },
      };
    } catch (error) {
      console.error("createPackagePaymentSessionFn error:", error);
      return {
        status: 500,
        body: { error: sanitizePaymentError(error) },
      };
    }
  });

const createProductCheckoutSchema = z.object({
  productId: z.string(),
});

export const createProductPaymentSessionFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(createProductCheckoutSchema)
  .handler(async ({ context, data }) => {
    const clientIp = getClientIp();

    try {
      const result = await paymentService.createProductPaymentSession({
        userId: context.auth.userId,
        productId: data.productId,
        clientIp,
      });

      if (result.status === "failure") {
        return {
          status: 400,
          body: { error: sanitizePaymentError(result.errorMessage) },
        };
      }

      return {
        status: 200,
        body: {
          paymentId: result.paymentId,
          token: result.token,
          paymentPageUrl: result.paymentPageUrl,
          checkoutFormContent: result.checkoutFormContent,
        },
      };
    } catch (error) {
      console.error("createProductPaymentSessionFn error:", error);
      return {
        status: 500,
        body: { error: sanitizePaymentError(error) },
      };
    }
  });
